import type { ReactNode } from 'react'
import { Pressable, StyleSheet } from 'react-native'
import Animated, {
  Easing,
  FadeIn,
  LinearTransition,
} from 'react-native-reanimated'

import { FigmaInfoCircleIcon } from '../icons/FigmaSystemIcons'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'
import { SkeletonBar } from './SkeletonBar'

const AnimatedBox = Animated.createAnimatedComponent(Box)

// The settled state's check mounting fresh — a plain fade, no scale or travel
// of its own. What moves is the figure it displaces.
const ICON_ENTERING = FadeIn.duration(420)
// The figure riding down as the check claims the line above it. The overshoot
// is where the design's bounce actually lives, not on the check. Both boxes the
// check displaces carry it — the block re-centres as it grows while the figure
// drops inside it, and animating only one of them snaps the other.
const DISPLACED_TRANSITION = LinearTransition.duration(420).easing(
  Easing.bezier(0.34, 1.56, 0.64, 1),
)

// Figma's summary amount, which `font/*` has no step to mirror. The tracking is
// the design's own -0.96 at 64, kept as a ratio so it travels with the size.
const AMOUNT_FONT_SIZE = 64
const LETTER_SPACING_RATIO = -0.96 / 64
// The design trims the amount to its cap box (45 at 64) — something RN can't do,
// since the line box is what it lays out. A line box shorter than the font's
// natural one gets the glyphs cut off on iOS, so the dead space is taken back as
// a negative margin instead, leaving the design's gaps between the cap boxes.
const LINE_HEIGHT_RATIO = 1.25
const CAP_BOX_RATIO = 45 / 64
const AMOUNT_LINE_HEIGHT = AMOUNT_FONT_SIZE * LINE_HEIGHT_RATIO
const AMOUNT_OVERHANG =
  (AMOUNT_FONT_SIZE * (LINE_HEIGHT_RATIO - CAP_BOX_RATIO)) / 2
// Roughly what a four-figure amount occupies, so the figure landing doesn't
// shift the block it is centred in.
const AMOUNT_SKELETON_WIDTH = 200
const TOKEN_SKELETON_WIDTH = 96

export interface ReceiveAmountSummaryProps {
  /** Sits above the label, e.g. the settled state's check. */
  icon?: ReactNode
  /** Names what the figure is, e.g. `You'll receive`. */
  label: string
  /**
   * The figure the screen leads with, in the user's currency. `undefined` until
   * the quote settles, which is what the placeholders stand in for.
   */
  usdLabel: string | undefined
  /** The same amount in the token that arrives, e.g. `99.78 USDC`. */
  tokenLabel: string | undefined
  /**
   * Whether a missing figure is still on its way. `false` leaves its place
   * empty: once the figures are frozen a placeholder would never resolve.
   */
  isQuotePending?: boolean
  /** Opens the breakdown. Omit to drop the affordance with it. */
  onPressBreakdown?: () => void
}

/**
 * What the withdrawal lands as: the figure, and the token it arrives in. The
 * screen's centrepiece once there is a quote to show.
 */
export function ReceiveAmountSummary({
  icon,
  label,
  usdLabel,
  tokenLabel,
  isQuotePending = true,
  onPressBreakdown,
}: ReceiveAmountSummaryProps) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()

  return (
    <AnimatedBox
      alignItems="center"
      justifyContent="center"
      layout={DISPLACED_TRANSITION}
      style={styles.root}
    >
      {icon ? <AnimatedBox entering={ICON_ENTERING}>{icon}</AnimatedBox> : null}
      <AnimatedBox
        alignItems="center"
        alignSelf="stretch"
        layout={DISPLACED_TRANSITION}
        style={styles.figure}
      >
        <Text
          variant="detail"
          weight="medium"
          style={{ color: theme.content.elements.secondary }}
        >
          {label}
        </Text>

        {usdLabel === undefined ? null : (
          <Text
            weight="medium"
            numberOfLines={1}
            style={[styles.amount, { color: theme.content.elements.primary }]}
          >
            {usdLabel}
          </Text>
        )}
        {usdLabel === undefined && isQuotePending ? (
          <SkeletonBar
            width={AMOUNT_SKELETON_WIDTH}
            height={AMOUNT_FONT_SIZE * CAP_BOX_RATIO}
            borderRadius={theme.borderRadii.element}
          />
        ) : null}

        {tokenLabel === undefined && isQuotePending ? (
          <SkeletonBar
            width={TOKEN_SKELETON_WIDTH}
            height={theme.textVariants.bodyMedium.lineHeight}
          />
        ) : null}
        {tokenLabel === undefined ? null : (
          <Pressable
            onPress={onPressBreakdown}
            disabled={!onPressBreakdown}
            accessibilityRole={onPressBreakdown ? 'button' : undefined}
            accessibilityLabel={
              onPressBreakdown
                ? t('withdrawAddress.breakdownTitle')
                : tokenLabel
            }
          >
            <Box
              flexDirection="row"
              alignItems="center"
              style={styles.tokenRow}
            >
              <Text
                variant="bodyMedium"
                style={{ color: theme.content.elements.secondary }}
              >
                {tokenLabel}
              </Text>
              {onPressBreakdown ? (
                <FigmaInfoCircleIcon
                  size={16}
                  color={theme.content.elements.secondary}
                />
              ) : null}
            </Box>
          </Pressable>
        )}
      </AnimatedBox>
    </AnimatedBox>
  )
}

const styles = StyleSheet.create({
  root: { gap: 24 },
  figure: { gap: 24 },
  amount: {
    fontSize: AMOUNT_FONT_SIZE,
    lineHeight: AMOUNT_LINE_HEIGHT,
    letterSpacing: AMOUNT_FONT_SIZE * LETTER_SPACING_RATIO,
    marginVertical: -AMOUNT_OVERHANG,
  },
  tokenRow: { gap: 6 },
})
